import typeahead from 'typeahead-standalone'
;(function ($) {
	'use strict'

	/**
	 * ArdentProviders - A jQuery plugin for filtering and displaying providers
	 * @class
	 */
	class ArdentProviders {
		/**
		 * Default configuration
		 * @static
		 */
		static DEFAULTS = {
			filters: {
				specialties: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
				languages: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
				gender: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
			},
			providers: [],
			pagination: {
				enabled: false,
				pageCount: 10,
				paged: false,
			},
			maxVisibleFilters: 5,
			debounceDelay: 300,
			cssClasses: {
				hidden: 'hide',
				selected: 'selected',
				disabled: 'disabled',
				fadeIn: 'fadeIn',
				fadeOut: 'fadeOut',
			},
			selectors: {
				stats: '.provider__stats',
				list: '.provider__list',
				item: '.provider__item',
				noMatches: '.provider__item__nomatches',
				count: '.provider__count',
				filters: '.provider__filters__container',
				listings: '.provider__listings',
			},
		}

		/**
		 * @constructor
		 * @param {HTMLElement} element - The container element
		 * @param {Object} options - Plugin options
		 */
		constructor(element, options) {
			this.element = element
			this.$element = $(element)
			this.targetId = this.$element.attr('id')

			// Merge settings
			this.settings = $.extend(true, {}, ArdentProviders.DEFAULTS, options)

			// State management
			this.state = {
				currentPage: 1,
				activeFilters: {},
				typeahead: {
					filterType: null,
					filterKey: null,
				},
				isInitialized: false,
			}

			// Cache for DOM elements
			this.cache = {}

			// Event handlers (for cleanup)
			this.eventHandlers = {
				popstate: null,
			}

			this.validateSettings()
		}

		/**
		 * Validate plugin settings
		 * @private
		 */
		validateSettings() {
			if (!this.targetId) {
				throw new Error('ArdentProviders: Container element must have an ID')
			}

			if (!Array.isArray(this.settings.providers)) {
				console.warn('ArdentProviders: providers should be an array, converting...')
				this.settings.providers = Object.values(this.settings.providers)
			}
		}

		/**
		 * Initialize the plugin
		 * @public
		 */
		init() {
			try {
				if (this.state.isInitialized) {
					console.warn('ArdentProviders: Already initialized')
					return this
				}

				if (this.settings.providers.length === 0) {
					console.warn('ArdentProviders: No providers provided')
					return this
				}

				this.cacheElements()
				this.createFilters()
				this.setupTypeahead()
				this.bindEvents()
				this.syncWithURL(false)

				this.cache.$stats.removeClass(this.settings.cssClasses.hidden)

				this.state.isInitialized = true
				return this
			} catch (error) {
				console.error('ArdentProviders: Initialization failed', error)
				return this
			}
		}

		/**
		 * Cache frequently accessed DOM elements
		 * @private
		 */
		cacheElements() {
			const sel = this.settings.selectors

			this.cache = {
				$element: this.$element,
				$stats: this.$element.find(sel.stats),
				$list: this.$element.find(sel.list),
				$items: this.$element.find(sel.item),
				$noMatches: this.$element.find(sel.noMatches),
				$count: this.$element.find(sel.count),
				$filters: $(sel.filters),
				$listings: this.$element.find(sel.listings),
			}
		}

		/**
		 * Bind event handlers
		 * @private
		 */
		bindEvents() {
			this.eventHandlers.popstate = () => this.syncWithURL(true)
			window.addEventListener('popstate', this.eventHandlers.popstate)
		}

		/**
		 * Create filter UI elements
		 * @private
		 */
		createFilters() {
			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled) {
					this.createFilterList(filter)
				}
			})
		}

		/**
		 * Create individual filter list
		 * @private
		 */
		createFilterList(filter) {
			// Setup clear button
			const $clearBtn = $(`.filter__clear[data-filter="${filter.dataFilter}"]`)
			if ($clearBtn.length) {
				$clearBtn.on('click', () => this.clearFilter(filter.dataFilter))
			}

			// Setup filter radio buttons
			filter.data.forEach(item => {
				const $radio = $(`input[type=radio][name="${filter.dataFilter}"][value="${item.key}"]`)
				if ($radio.length) {
					$radio.on('change', e => {
						this.setFilter(e.target.name, e.target.value)
					})
				}
			})

			// Setup "Show More" toggle
			if (filter.data.length > this.settings.maxVisibleFilters) {
				const $showMoreLink = $(`.show-more-${filter.dataFilter} a`)
				if ($showMoreLink.length) {
					$showMoreLink.on('click', e => {
						this.toggleFilterVisibility(e, e.currentTarget, filter.dataFilter)
					})
				}
			}
		}

		/**
		 * Toggle filter visibility (Show More/Less)
		 * @private
		 */
		toggleFilterVisibility(event, element, target) {
			event.preventDefault()

			const $el = $(element)
			const toggle = $el.data('toggle')
			const { fadeIn, fadeOut, hidden } = this.settings.cssClasses

			if (toggle === 'more') {
				$(`label[for^="${target}"][data-show="more"]`)
					.removeClass(`${hidden} ${fadeOut}`)
					.addClass(fadeIn)
					.attr('data-show', 'less')

				$el.data('toggle', 'less').text('Show Less')
			} else {
				$(`label[for^="${target}"][data-show="less"]`)
					.addClass(hidden)
					.removeClass(fadeIn)
					.addClass(fadeOut)
					.attr('data-show', 'more')

				$el.data('toggle', 'more').text('Show More')

				const $heading = $(`#${target}-heading`)
				if ($heading.length) {
					$heading[0].scrollIntoView({ behavior: 'smooth' })
				}
			}
		}

		/**
		 * Sync filters with URL parameters
		 * @private
		 */
		syncWithURL(shouldScroll = false) {
			const urlParams = new URLSearchParams(window.location.search)

			this.clearAllFilters()

			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled) {
					const paramName = this.toKebabCase(filter.dataFilter)
					const paramValue = urlParams.get(paramName)

					if (paramValue) {
						this.setFilter(filter.dataFilter, paramValue, false)
					}
				}
			})

			if (this.settings.pagination.enabled) {
				const pageParam = urlParams.get('pg')
				if (pageParam) {
					this.settings.pagination.paged = true
					this.state.currentPage = parseInt(pageParam, 10)
				} else {
					this.settings.pagination.paged = false
					this.state.currentPage = 1
				}
			}

			this.applyFilters(true)

			if (shouldScroll) {
				this.scrollToStats()
			}
		}

		/**
		 * Set a filter value
		 * @public
		 */
		setFilter(filterName, value = '', shouldScroll = true, resetOthers = false) {
			if (resetOthers) {
				this.clearAllFilters()
			}

			const { hidden, disabled } = this.settings.cssClasses

			this.state.activeFilters[filterName] = value

			$(`.provider-filters__${filterName} .filter__clear`).removeClass(hidden)

			const $radios = $(`input[name*="${filterName}"]`)
			$radios.each((index, radio) => {
				const $radio = $(radio)
				const $label = $radio.parent()

				if (radio.value === value) {
					$radio.prop('checked', true).prop('disabled', false)
					$label.removeClass(disabled)

					if (filterName === radio.name) {
						this.addFilterTag(filterName, $radio.next().html())
					}
				} else {
					$radio.prop('disabled', true)
					$label.addClass(disabled)
				}
			})

			this.applyFilters()

			if (shouldScroll) {
				this.scrollToStats()
			}

			this.$element.trigger('filterChanged', { filterName, value })
		}

		/**
		 * Clear a specific filter
		 * @public
		 */
		clearFilter(filterName, shouldScroll = true) {
			const { hidden, disabled } = this.settings.cssClasses
			let filterValue = ''

			const $radios = $(`input[name*="${filterName}"]`)
			$radios.each((index, radio) => {
				const $radio = $(radio)
				if ($radio.prop('checked')) {
					filterValue = radio.value
				}
				$radio.prop('checked', false).prop('disabled', false)
				$radio.parent().removeClass(disabled)
			})

			$(`.filter__clear[data-filter="${filterName}"]`).addClass(hidden)
			this.cache.$filters.find(`span[data-filter="${filterName}"]`).remove()

			delete this.state.activeFilters[filterName]

			if (this.state.typeahead.filterType === filterName && filterValue === this.state.typeahead.filterKey) {
				this.clearTypeahead()
			}

			this.applyFilters()

			if (shouldScroll) {
				this.scrollToStats()
			}

			this.$element.trigger('filterCleared', { filterName })
		}

		/**
		 * Clear all filters
		 * @public
		 */
		clearAllFilters() {
			const { hidden, disabled } = this.settings.cssClasses

			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled) {
					const $radios = $(`input[name*="${filter.dataFilter}"]`)
					$radios.prop('checked', false).prop('disabled', false)
					$radios.parent().removeClass(disabled)

					$(`.filter__clear[data-filter="${filter.dataFilter}"]`).addClass(hidden)
				}
			})

			this.cache.$filters.find('.provider__filters__filter').remove()
			this.state.activeFilters = {}

			this.$element.trigger('filtersCleared')
		}

		/**
		 * Apply current filters to providers
		 * @public
		 */
		applyFilters(bypassHistory = false) {
			const filteredProviders = this.getFilteredProviders()
			this.updateFilterUI(filteredProviders)
			this.updateProviderList(filteredProviders)
			this.updateStats(filteredProviders.length)
			this.paginate()

			if (!bypassHistory) {
				this.updateURL()
			}
		}

		/**
		 * Get filtered providers based on active filters
		 * @private
		 */
		getFilteredProviders() {
			let providers = [...this.settings.providers]

			Object.entries(this.state.activeFilters).forEach(([filterName, filterValue]) => {
				providers = providers.filter(provider => {
					const providerValue = provider[filterName]
					return Array.isArray(providerValue)
						? providerValue.includes(filterValue)
						: this.toKebabCase(String(providerValue)) === filterValue
				})
			})

			return providers
		}

		/**
		 * Update filter UI based on available providers
		 * @private
		 */
		updateFilterUI(filteredProviders) {
			Object.values(this.settings.filters).forEach(filter => {
				if (!filter.enabled) return

				const $selectedFilter = $(`[type=radio][name^="${filter.dataFilter}"]:checked`)
				const filterGroups = this.categorizeFilters(filter, filteredProviders, $selectedFilter.length > 0)

				this.renderFilterGroups(filter, filterGroups)
			})
		}

		/**
		 * Categorize filters into available and unavailable
		 * @private
		 */
		categorizeFilters(filter, filteredProviders, hasSelection) {
			const available = []
			const unavailable = []

			if (hasSelection) {
				const $selectedFilter = $(`[type=radio][name^="${filter.dataFilter}"]:checked`)
				available.push($selectedFilter[0])

				$(`[type=radio][name^="${filter.dataFilter}"]:not(:checked)`).each((index, radio) => {
					unavailable.push(radio)
				})
			} else {
				filter.data.forEach(item => {
					const hasProviders = filteredProviders.some(provider => {
						const filterValue = provider[filter.dataFilter]
						return Array.isArray(filterValue)
							? filterValue.includes(item.key)
							: String(filterValue) === item.key
					})

					const radios = $(`[type=radio][name^="${filter.dataFilter}"][value="${item.key}"]`).toArray()

					if (hasProviders) {
						available.push(...radios)
					} else {
						unavailable.push(...radios)
					}
				})
			}

			return { available, unavailable }
		}

		/**
		 * Render filter groups in the UI
		 * @private
		 */
		renderFilterGroups(filter, groups) {
			const { disabled, hidden, fadeIn, fadeOut } = this.settings.cssClasses
			const $showMoreLink = $(`.show-more-${filter.dataFilter} a`)
			const showMore = $showMoreLink.length ? $showMoreLink.data('toggle') : ''

			let filterLoop = 0

			groups.available.forEach(radio => {
				const $radio = $(radio)
				const $label = $radio.parent()

				$radio.prop('disabled', false)
				$label.removeClass(disabled).removeClass(`${fadeIn} ${fadeOut}`)

				if (filterLoop >= this.settings.maxVisibleFilters) {
					$label.attr('data-show', showMore)
					$label.toggleClass(hidden, showMore === 'more')
				} else {
					$label.attr('data-show', '').removeClass(hidden)
				}

				$(`.provider__filters .provider-filters__${filter.dataFilter} .provider-filters__filters`).append(
					$label
				)
				filterLoop++
			})

			groups.unavailable.forEach(radio => {
				const $radio = $(radio)
				const $label = $radio.parent()

				$radio.prop('disabled', true)
				$label.addClass(disabled).removeClass(`${fadeIn} ${fadeOut}`)

				if (filterLoop >= this.settings.maxVisibleFilters) {
					$label.attr('data-show', showMore)
					$label.toggleClass(hidden, showMore === 'more')
				} else {
					$label.attr('data-show', '').removeClass(hidden)
				}

				$(`.provider__filters .provider-filters__${filter.dataFilter} .provider-filters__filters`).append(
					$label
				)
				filterLoop++
			})
		}

		/**
		 * Update provider list visibility
		 * @private
		 */
		updateProviderList(filteredProviders) {
			const { hidden } = this.settings.cssClasses

			this.cache.$items.addClass(hidden).removeClass('pagination__hidden')

			filteredProviders.forEach(provider => {
				const $item = this.$element.find(`[data-provider-id="${provider.entityId}"]`)
				$item.removeClass(hidden)
			})
		}

		/**
		 * Update statistics display
		 * @private
		 */
		updateStats(count) {
			const { hidden } = this.settings.cssClasses
			const totalItems = this.cache.$items.not(this.cache.$noMatches).length
			const hiddenItems = this.cache.$items.not(this.cache.$noMatches).filter(`.${hidden}`).length

			this.cache.$noMatches.toggleClass(hidden, totalItems !== hiddenItems)

			if (this.cache.$count.length) {
				this.cache.$count.text(`${count} Results Found`)
			}
		}

		/**
		 * Handle pagination
		 * @private
		 */
		paginate() {
			if (!this.settings.pagination.enabled) return

			const $pagination = this.$element.find('.pagination')
			$pagination.remove()

			const $visibleItems = this.cache.$items
				.not(`.${this.settings.cssClasses.hidden}`)
				.not(this.cache.$noMatches)

			if ($visibleItems.length <= this.settings.pagination.pageCount) return

			let page = this.state.currentPage
			let shouldScroll = false

			if (this.settings.pagination.paged) {
				shouldScroll = true
				this.settings.pagination.paged = false
			} else {
				this.state.currentPage = 1
				page = 1
			}

			const pageCount = this.settings.pagination.pageCount
			const startIndex = (page - 1) * pageCount
			const endIndex = page * pageCount
			const totalPages = Math.ceil($visibleItems.length / pageCount)

			$visibleItems.addClass('pagination__hidden')
			$visibleItems.slice(startIndex, endIndex).removeClass('pagination__hidden')

			this.createPaginationControls(totalPages, page)

			if (shouldScroll) {
				this.scrollToStats()
			}
		}

		/**
		 * Create pagination controls
		 * @private
		 */
		createPaginationControls(totalPages, currentPage) {
			const $pagination = $('<div class="pagination"></div>')

			for (let i = 1; i <= totalPages; i++) {
				const $link = $('<a class="pagination__link"></a>')
					.text(i)
					.data('page', i)
					.toggleClass('current', i === currentPage)
					.on('click', e => {
						e.preventDefault()
						this.handlePaginationClick($(e.currentTarget).data('page'))
					})

				$pagination.append($link)
			}

			this.cache.$list.append($pagination)
		}

		/**
		 * Handle pagination link click
		 * @private
		 */
		handlePaginationClick(page) {
			this.state.currentPage = page
			this.settings.pagination.paged = true

			const urlParams = new URLSearchParams(window.location.search)

			if (page === 1) {
				urlParams.delete('pg')
			} else {
				urlParams.set('pg', page)
			}

			const newUrl = urlParams.toString() ? `?${urlParams.toString()}` : window.location.pathname
			history.pushState(null, null, newUrl)

			this.paginate()
		}

		/**
		 * Display a single provider (used by typeahead)
		 * @public
		 */
		displayProvider(provider, bypassHistory = false) {
			const { hidden } = this.settings.cssClasses

			this.cache.$items.addClass(hidden).removeClass('pagination__hidden')

			const $providerItem = this.$element.find(`[data-provider-id="${provider.key}"]`)
			$providerItem.removeClass(hidden)

			const visibleCount = $providerItem.length
			this.updateStats(visibleCount)
			this.paginate()

			if (!bypassHistory) {
				this.updateURL()
			}
		}

		/**
		 * Setup typeahead search
		 * @private
		 */
		setupTypeahead() {
			const $searchInput = $('#providerSearchInput')
			const $searchForm = $('#ardent-provider-search')
			const $resetButton = $('.typeahead_button')

			if (!$searchInput.length) return

			const typeaheadData = this.buildTypeaheadData()

			if (typeaheadData.length === 0) return

			typeahead({
				input: $searchInput[0],
				source: {
					local: typeaheadData,
					keys: ['value'],
					identity: item => `${item.key}`,
					groupKey: 'sortType',
				},
				limit: 100,
				highlight: true,
				preventSubmit: true,
				onSubmit: (e, selectedSuggestion) => {
					e.preventDefault()
					if (selectedSuggestion) return
				},
				display: (item, event) => {
					if (event) {
						this.handleTypeaheadSelection(item)
						$resetButton.removeClass(this.settings.cssClasses.hidden)
						$searchInput.addClass('has-reset')
					}
					return item.value
				},
				templates: {
					group: groupName => this.getTypeaheadGroupTemplate(groupName),
					suggestion: item => this.getTypeaheadSuggestionTemplate(item),
					notFound: resultSet => {
						this.state.typeahead = { filterType: null, filterKey: null }
						return `<span>No results found for '${resultSet.query}'</span>`
					},
				},
				hooks: {
					updateHits: async resultSet => resultSet,
				},
			})

			// Add aria-label to the hint input injected by typeahead-standalone.
			// The library provides no configuration option for this, so we patch
			// it after initialisation.
			const hintInput = $searchInput[0].parentElement?.querySelector('.tt-hint')
			if (hintInput) {
				hintInput.setAttribute('aria-label', 'Search suggestion')
			}

			$searchForm.on('reset', e => {
				e.preventDefault()
				this.clearTypeahead()
				this.clearAllFilters()
				this.applyFilters()
			})
		}

		/**
		 * Build typeahead data from providers and filters
		 * @private
		 */
		buildTypeaheadData() {
			const data = []

			// Add individual providers
			this.settings.providers.forEach(provider => {
				data.push({
					key: provider.entityId,
					value: provider.providerName,
					filterType: 'provider',
					filterTitle: 'Providers',
					sortType: 'a',
					...provider,
				})
			})

			// Add filter terms
			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled && filter.dataFilter !== 'gender') {
					const sortType = this.getFilterSortType(filter.dataFilter)

					filter.data.forEach(item => {
						data.push({
							...item,
							filterType: filter.dataFilter,
							filterTitle: filter.title,
							sortType,
						})
					})
				}
			})

			return data
		}

		/**
		 * Get sort type for filter grouping
		 * @private
		 */
		getFilterSortType(filterType) {
			const sortMap = {
				specialties: 'b',
				languages: 'c',
			}
			return sortMap[filterType] || 'd'
		}

		/**
		 * Handle typeahead selection
		 * @private
		 */
		handleTypeaheadSelection(item) {
			this.state.typeahead = {
				filterKey: item.key,
				filterType: item.filterType,
			}

			if (item.filterType === 'provider') {
				this.clearAllFilters()
				this.displayProvider(item)
			} else {
				this.setFilter(item.filterType, item.key, true, true)
			}
		}

		/**
		 * Clear typeahead search
		 * @private
		 */
		clearTypeahead() {
			const $searchInput = $('#providerSearchInput')
			const $resetButton = $('.typeahead_button')

			this.state.typeahead = { filterType: null, filterKey: null }
			$searchInput.val('').removeClass('has-reset')
			$resetButton.addClass(this.settings.cssClasses.hidden)
		}

		/**
		 * Get typeahead group template
		 * @private
		 */
		getTypeaheadGroupTemplate(groupName) {
			const groupMap = {
				a: { label: 'Providers', attribute: 'provider' },
				b: { label: 'Specialty', attribute: 'specialties' },
				c: { label: 'Language', attribute: 'languages' },
			}

			const group = groupMap[groupName] || { label: 'Other', attribute: 'other' }
			return `<div class="custom-group" data-filter="${group.attribute}">${group.label}</div>`
		}

		/**
		 * Get typeahead suggestion template
		 * @private
		 */
		getTypeaheadSuggestionTemplate(item) {
			if (item.filterType === 'provider') {
				return `<div class="tt-suggestion" data-key="${item.key}">
					${item.providerName}
				</div>`
			}
			return `<div class="tt-suggestion" data-key="${item.key}">${item.value}</div>`
		}

		/**
		 * Add filter tag to UI
		 * @private
		 */
		addFilterTag(filterName, label) {
			const $tag = $('<span class="provider__filters__filter"></span>')
				.attr('data-filter', filterName)
				.html(label)
				.on('click', e => {
					this.clearFilter($(e.currentTarget).data('filter'))
				})

			this.cache.$filters.append($tag)
		}

		/**
		 * Update URL with current filter state
		 * @private
		 */
		updateURL() {
			const params = new URLSearchParams()

			Object.entries(this.state.activeFilters).forEach(([filterName, filterValue]) => {
				params.set(this.toKebabCase(filterName), filterValue)
			})

			if (this.state.currentPage > 1) {
				params.set('pg', this.state.currentPage)
			}

			const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
			history.pushState(null, null, newUrl)
		}

		/**
		 * Scroll to stats section
		 * @private
		 */
		scrollToStats() {
			if (this.cache.$stats.length) {
				this.cache.$stats[0].scrollIntoView({ behavior: 'smooth' })
			}
		}

		/**
		 * Convert string to kebab-case
		 * @private
		 */
		toKebabCase(str) {
			if (!str) return ''
			return str
				.replace(/\s*-\s*/g, '-')
				.replace(/_/g, ' ')
				.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
				.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
				.toLowerCase()
				.replace(/[^a-z0-9-]+/g, '-')
				.replace(/^-+|-+$/g, '')
				.replace(/-+/g, '-')
		}

		/**
		 * Debounce function
		 * @private
		 */
		debounce(func, wait) {
			let timeout
			return function executedFunction(...args) {
				const later = () => {
					clearTimeout(timeout)
					func(...args)
				}
				clearTimeout(timeout)
				timeout = setTimeout(later, wait)
			}
		}

		/**
		 * Destroy the plugin instance and clean up
		 * @public
		 */
		destroy() {
			if (this.eventHandlers.popstate) {
				window.removeEventListener('popstate', this.eventHandlers.popstate)
			}

			this.$element.find('*').off()
			this.cache = {}
			this.$element.removeData('ardentProviders')
			this.state.isInitialized = false
		}

		/**
		 * Get current plugin state (for debugging)
		 * @public
		 */
		getState() {
			return {
				...this.state,
				providerCount: this.settings.providers.length,
				isInitialized: this.state.isInitialized,
			}
		}
	}

	/**
	 * jQuery plugin wrapper
	 */
	$.fn.ardentProviders = function (options) {
		return this.each(function () {
			const $this = $(this)

			if (!$this.data('ardentProviders')) {
				const instance = new ArdentProviders(this, options)
				$this.data('ardentProviders', instance.init())
				$this.trigger('providersLoaded')
			}
		})
	}

	// Expose the class for advanced usage
	$.ardentProviders = ArdentProviders
})(jQuery)
